<script setup lang="ts">
import { computed } from "vue";

/** Radial gauge. Geometry mirrors the handoff: r = (size - thickness) / 2. */
const props = withDefaults(
  defineProps<{
    value?: number;
    max?: number;
    size?: number;
    thickness?: number;
    color?: string;
    track?: string;
  }>(),
  {
    value: 0,
    max: 100,
    size: 120,
    thickness: 12,
    color: "var(--fuel-carb)",
    track: "var(--surface-sunken)",
  },
);

const r = computed(() => (props.size - props.thickness) / 2);
const circumference = computed(() => 2 * Math.PI * r.value);
const fraction = computed(() => Math.max(0, Math.min(1, props.value / props.max)));
const dashoffset = computed(() => circumference.value * (1 - fraction.value));
</script>
<template>
  <div
    class="ring"
    :style="{ width: `${size}px`, height: `${size}px` }"
  >
    <svg
      :width="size"
      :height="size"
      style="transform: rotate(-90deg)"
      aria-hidden="true"
    >
      <circle
        :cx="size / 2"
        :cy="size / 2"
        :r="r"
        fill="none"
        :stroke="track"
        :stroke-width="thickness"
      />
      <circle
        class="ring__value"
        :cx="size / 2"
        :cy="size / 2"
        :r="r"
        fill="none"
        :stroke="color"
        :stroke-width="thickness"
        stroke-linecap="round"
        :stroke-dasharray="circumference"
        :stroke-dashoffset="dashoffset"
      />
    </svg>
    <div class="ring__center">
      <slot />
    </div>
  </div>
</template>
<style scoped>
.ring { position: relative; }
.ring__center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
}
.ring__value { transition: stroke-dashoffset var(--dur-slower, 520ms) var(--ease-out); }
@media (prefers-reduced-motion: reduce) {
  .ring__value { transition: none; }
}
</style>
